import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect } from 'react-redux';

import { validateEInvoiceRequest } from '@epi-api/eInvoice';
import { FormFields, useAsyncValidator } from '@epi-forms/helpers';

import { getRecaptchaToken } from '@epi-helpers/ReCaptchaHelper';
import { getReCaptchaSiteKey } from '@epi-selectors/settings';
import { EInvoiceProps } from '@epi-models/forms/Controls';

import { EInvoiceContext } from './EInvoiceContext';

function EInvoice({
  reCaptchaSiteKey,
  forwardedRef,
  children
}: Readonly<EInvoiceProps>) {
  const {
    formState: { errors },
    getValues
  } = useFormContext();

  const eInvoiceValidator = async (abortController): Promise<void> => {
    if (!reCaptchaSiteKey) {
      throw new Error('ReCaptcha site key is missing');
    }

    const reCaptchaToken = await getRecaptchaToken(
      reCaptchaSiteKey,
      'einvoice_validation'
    );

    const eInvoiceAddress = getValues(FormFields.EInvoiceAddress);
    const operator = getValues(FormFields.Operator);

    const { valid } = await validateEInvoiceRequest({
      eInvoiceAddress,
      operator,
      reCaptchaToken
    });

    if (!valid) {
      throw new Error('EInvoice is not valid');
    }
  };

  const validatorGetValue = () => {
    return {
      [FormFields.EInvoiceAddress]: getValues(FormFields.EInvoiceAddress),
      [FormFields.Operator]: getValues(FormFields.Operator)
    };
  };

  const validatorCanValidate = () => {
    return (
      !!getValues(FormFields.EInvoiceAddress) &&
      !!getValues(FormFields.Operator) &&
      !errors[FormFields.EInvoiceAddress] &&
      !errors[FormFields.Operator]
    );
  };

  const validateEInvoice = useAsyncValidator({
    fieldName: FormFields.EInvoice,
    validator: eInvoiceValidator,
    validatorName: FormFields.EInvoice,
    getValueFn: validatorGetValue,
    canValidateFn: validatorCanValidate
  });

  useImperativeHandle(forwardedRef, () => ({
    validateEInvoice
  }));

  const contextValue = useMemo(
    () => ({ validateEInvoice }),
    [validateEInvoice]
  );

  return (
    <EInvoiceContext.Provider value={contextValue}>
      {children}
    </EInvoiceContext.Provider>
  );
}

const mapStateToProps = state => ({
  reCaptchaSiteKey: getReCaptchaSiteKey(state)
});

const ConnectedEInvoice = connect(mapStateToProps)(EInvoice);

const ForwardedRefEInvoice = forwardRef<any, EInvoiceProps>((props, ref) => (
  <ConnectedEInvoice {...props} forwardedRef={ref} />
));

ForwardedRefEInvoice.displayName = 'ForwardedRefEInvoice';

export { ForwardedRefEInvoice as EInvoice };
